import React, { useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CommentEditor from './CommentEditor';
import { IFigureDto } from '../models/IFigureDto';
import FigurService from '../services/figureService';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'oidc-react';
import { getUserEmail, getUserDisplayName } from '../utils/auth';
import { emitAppEvent } from '../events/appEvents';

interface ItemCommentProps {
  imageData: IFigureDto;
  collectionId: number;
  onUpdate?: (data: IFigureDto) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  collections?: Array<{ id: number; name: string; color?: string }>;
  readOnly?: boolean; // If true, disable editing of comments and tags
}

const ItemComment: React.FC<ItemCommentProps> = ({
  imageData,
  collectionId,
  onUpdate,
  expanded,
  onExpandedChange,
  collections = [],
  readOnly = false,
}) => {
  const [customerCommentJson, setCustomerCommentJson] = useState(imageData.customerComment || '');
  const queryClient = useQueryClient();
  const auth = useAuth();
  const savingRef = useRef(false);

  useEffect(() => {
    if (savingRef.current) return;
    setCustomerCommentJson(imageData.customerComment || '');
  }, [imageData.imageId, imageData.customerComment]);

  const handleSave = async (json?: string) => {
    try {
      savingRef.current = true;

      // Use provided JSON or fall back to current state
      const commentJson = json || customerCommentJson;

      const figure = {
        ...imageData,
        customerComment: commentJson,
      };

      await FigurService.saveFigure(figure, collectionId);

      const freshFigure = await FigurService.getFigureByIdAndCollection(imageData.imageId, collectionId);

      // Clear savingRef BEFORE calling onUpdate so useEffect can fire when props change
      savingRef.current = false;

      if (onUpdate) {
        onUpdate(freshFigure);
      }

      emitAppEvent('figureUpdated', freshFigure);
      queryClient.invalidateQueries({ queryKey: ['figures-grid', collectionId] });
      queryClient.invalidateQueries({ queryKey: ['figures', collectionId] });
      queryClient.invalidateQueries({ queryKey: ['figures-grid', 0] });
      queryClient.invalidateQueries({ queryKey: ['figures', 0] });

      const applyCacheUpdate = (key: readonly unknown[]) => {
        type CachedData = { pages: { data: IFigureDto[] }[] } | undefined;
        queryClient.setQueryData<CachedData>(key, (old) => {
          if (!old || !old.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((f) =>
                f.imageId === freshFigure.imageId
                  ? {
                      ...f,
                      customerComment: freshFigure.customerComment,
                    }
                  : f,
              ),
            })),
          };
        });
      };
      const cachedGridKeys = queryClient.getQueriesData({ queryKey: ['figures-grid', collectionId] }).map(([key]) => key);
      cachedGridKeys.forEach((key) => applyCacheUpdate(key as readonly unknown[]));
      const cachedListKeys = queryClient.getQueriesData({ queryKey: ['figures', collectionId] }).map(([key]) => key);
      cachedListKeys.forEach((key) => applyCacheUpdate(key as readonly unknown[]));
      const favGridKeys = queryClient.getQueriesData({ queryKey: ['figures-grid', 0] }).map(([key]) => key);
      favGridKeys.forEach((key) => applyCacheUpdate(key as readonly unknown[]));
      const favListKeys = queryClient.getQueriesData({ queryKey: ['figures', 0] }).map(([key]) => key);
      favListKeys.forEach((key) => applyCacheUpdate(key as readonly unknown[]));

      // Note: Local state will be updated via useEffect when imageData prop changes
    } catch (error) {
      console.error('Error saving figure:', error);
      savingRef.current = false; // Clear on error too
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, newExpanded) => onExpandedChange?.(newExpanded)}
      sx={{
        mt: 3,
        backgroundColor: 'transparent',
        '&.MuiPaper-root': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
        '& .MuiAccordionSummary-root': {
          backgroundColor: 'transparent',
          minHeight: 28,
          padding: '4px 16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          '&.Mui-expanded': {
            minHeight: 28,
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
            minHeight: 28,
            '&.Mui-expanded': {
              margin: 0,
              minHeight: 28,
            },
          },
        },
        '& .MuiAccordionDetails-root': {
          backgroundColor: 'transparent',
        },
      }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="comments-content" id="comments-header">
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 400,
            fontSize: '0.875rem',
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
          }}>
          Kommentarer
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <CommentEditor
          value={customerCommentJson}
          onChange={setCustomerCommentJson}
          onSave={handleSave}
          currentUserEmail={getUserEmail(auth.userData)}
          currentUserName={getUserDisplayName(auth.userData)}
          collectionId={collectionId}
          collections={collections}
          readOnly={readOnly}
          showAllComments={readOnly}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default ItemComment;
